import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Componente: grilla de signos vitales dibujable a mano alzada.
 *
 * Dos variantes de grilla:
 *  - `v1`: formato previo (eje 0–240, filas INICIO / SAT O2 / Et CO2 / T° / O2 /
 *    N2O). Se conserva SIN cambios para renderizar registros ya firmados
 *    exactamente como se dibujaron.
 *  - `v2`: formato nuevo del papel Endomedical (filas HORA / SpO2 / EKG / L.E.V.
 *    arriba y eje vertical 200 → 0 en pasos de 10).
 *
 * El anestesiólogo dibuja sobre la grilla con el dedo o stylus. Además, con
 * `withSymbols`, aparece una paleta lateral de símbolos clínicos que se pueden
 * ARRASTRAR a la gráfica (se estampan; la paleta no se consume, se pueden poner
 * cuantos se quiera). Trazos y símbolos comparten historial: "Deshacer" quita
 * el último y "Borrar dibujo" limpia todo. El resultado se exporta como dataURL
 * PNG (fondo transparente), así que los símbolos quedan guardados y se ven igual
 * en el detalle y la impresión.
 */
export interface DrawableVitalsChartProps {
  /** Valor inicial (dataURL PNG). En edición se carga UNA vez como base. */
  value?: string | null;
  /** Se invoca cada vez que cambia el dibujo (fin de trazo, estampa, deshacer, borrar). */
  onChange?(dataUrl: string): void;
  /** Color del trazo del usuario. Default rojo clínico. */
  strokeColor?: string;
  /** Grosor del trazo. */
  strokeWidth?: number;
  /** Alto en píxeles CSS. Default 540 (~la altura del bloque en el PDF). */
  height?: number;
  /** Solo lectura: muestra la grilla + el dibujo, sin permitir editar. */
  readOnly?: boolean;
  /** Variante de grilla. Default `v1` (compatibilidad con registros previos). */
  variant?: 'v1' | 'v2';
  /** Muestra la paleta de símbolos arrastrables al costado (solo en edición). */
  withSymbols?: boolean;
}

const GRID_BG_COLOR = '#f8fafc';
const GRID_LINE_LIGHT = '#e5e7eb';
const GRID_LINE_MEDIUM = '#cbd5e1';
const GRID_LINE_DARK = '#94a3b8';
const LABEL_COLOR = '#475569';
const LABEL_FONT = '11px Inter, system-ui, sans-serif';

// --- v1 (formato previo) ---
const Y_LABELS = ['240', '220', '200', '180', '160', '120', '100', '80', '60', '40', '20', '0'];
const FOOTER_ROWS = ['INICIO', 'SAT O2', 'Et CO2', 'T°', 'O2', 'N2O'];
const X_COLUMNS = 16;

// --- v2 (papel nuevo) ---
const HEADER_ROWS_V2 = ['HORA', 'SpO2', 'EKG', 'L.E.V.'];
const Y_MAX_V2 = 200;
const Y_STEP_V2 = 10;
const X_COLUMNS_V2 = 24;

// Símbolos clínicos arrastrables a la gráfica (formato del papel Endomedical).
export interface ChartSymbol {
  readonly id: string;
  readonly glyph: string;
  readonly label: string;
}
export const ANESTHESIA_CHART_SYMBOLS: readonly ChartSymbol[] = [
  { id: 'v-abajo', glyph: '∨', label: 'Sistólica (∨)' },
  { id: 'v-arriba', glyph: '∧', label: 'Diastólica (∧)' },
  { id: 'circulo', glyph: '○', label: 'Círculo' },
  { id: 'equis', glyph: '✕', label: 'Equis' },
  { id: 'p-circulo', glyph: 'Ⓟ', label: 'P en círculo' },
  { id: 'x-circulo', glyph: 'Ⓧ', label: 'X en círculo' },
  { id: 'circulo-2', glyph: '○', label: 'Círculo' },
  { id: 'triangulo', glyph: '△', label: 'Triángulo' },
  { id: 'masculino', glyph: '♂', label: 'Masculino' },
  { id: 'femenino', glyph: '♀', label: 'Femenino' },
];
/** Tamaño (px CSS) con que se estampa un símbolo en la gráfica. */
const STAMP_CSS_PX = 20;
const SYMBOL_FONT_STACK = '"Segoe UI Symbol", "Apple Symbols", "Noto Sans Symbols2", system-ui, sans-serif';

/** Un punto normalizado (0..1) respecto al canvas. */
type NPoint = { nx: number; ny: number };
/** Operación del historial: un trazo o un símbolo estampado. */
type Op =
  | { kind: 'stroke'; points: NPoint[] }
  | { kind: 'stamp'; glyph: string; nx: number; ny: number };

export function DrawableVitalsChart({
  value,
  onChange,
  strokeColor = '#dc2626',
  strokeWidth = 2,
  height = 540,
  readOnly = false,
  variant = 'v1',
  withSymbols = false,
}: DrawableVitalsChartProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef<boolean>(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  // Historial de operaciones (trazos + estampas), para deshacer.
  const opsRef = useRef<Op[]>([]);
  const currentRef = useRef<NPoint[]>([]);
  // Imagen base (dibujo cargado de un borrador) sobre la que se apila.
  const baselineImgRef = useRef<HTMLImageElement | null>(null);
  const baselineLoadedRef = useRef<boolean>(false);
  // Último dataURL que emitimos — para NO recargar nuestro propio eco como base.
  const lastEmittedRef = useRef<string>('');
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [opCount, setOpCount] = useState<number>(0);

  // Arrastre de símbolo desde la paleta.
  const dragGlyphRef = useRef<string | null>(null);
  const [drag, setDrag] = useState<{ glyph: string; x: number; y: number } | null>(null);

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  // Resize observer: nos adaptamos al ancho del contenedor del canvas.
  useEffect(() => {
    const el = wrapperRef.current;
    if (el === null) return;
    const apply = (): void => {
      const w = Math.max(280, el.clientWidth);
      setSize({ w, h: height });
    };
    apply();
    const obs = new ResizeObserver(apply);
    obs.observe(el);
    return () => obs.disconnect();
  }, [height]);

  // Pinta el fondo (grilla + etiquetas) cuando cambia el tamaño o la variante.
  useEffect(() => {
    const bg = bgCanvasRef.current;
    if (bg === null || size.w === 0) return;
    paintGrid(bg, size.w, size.h, variant);
  }, [size, variant]);

  const drawStamp = useCallback(
    (ctx: CanvasRenderingContext2D, glyph: string, x: number, y: number): void => {
      ctx.save();
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `bold ${Math.round(STAMP_CSS_PX * dpr)}px ${SYMBOL_FONT_STACK}`;
      ctx.fillText(glyph, x, y);
      ctx.restore();
    },
    [dpr],
  );

  // Redibuja el canvas: imagen base + todas las operaciones del historial.
  const redrawAll = useCallback((): void => {
    const c = drawCanvasRef.current;
    if (c === null) return;
    const ctx = c.getContext('2d');
    if (ctx === null) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const base = baselineImgRef.current;
    if (base !== null) ctx.drawImage(base, 0, 0, c.width, c.height);
    for (const op of opsRef.current) {
      if (op.kind === 'stamp') {
        drawStamp(ctx, op.glyph, op.nx * c.width, op.ny * c.height);
        continue;
      }
      const pts = op.points;
      if (pts.length === 0) continue;
      ctx.strokeStyle = strokeColor;
      ctx.fillStyle = strokeColor;
      ctx.lineWidth = strokeWidth * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(pts[0]!.nx * c.width, pts[0]!.ny * c.height, (strokeWidth * dpr) / 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(pts[0]!.nx * c.width, pts[0]!.ny * c.height);
      for (let i = 1; i < pts.length; i += 1) {
        ctx.lineTo(pts[i]!.nx * c.width, pts[i]!.ny * c.height);
      }
      ctx.stroke();
    }
  }, [strokeColor, strokeWidth, dpr, drawStamp]);

  const emit = useCallback((): void => {
    const c = drawCanvasRef.current;
    if (c === null || onChange === undefined) return;
    const isEmpty = baselineImgRef.current === null && opsRef.current.length === 0;
    const payload = isEmpty ? '' : c.toDataURL('image/png');
    lastEmittedRef.current = payload;
    onChange(payload);
  }, [onChange]);

  // Carga del `value`:
  //  - readOnly: siempre pinta la imagen recibida (registro guardado).
  //  - edición: carga el dibujo del borrador UNA vez como base; luego se apila
  //    lo nuevo encima (para que "deshacer" solo afecte lo nuevo).
  useEffect(() => {
    const c = drawCanvasRef.current;
    if (c === null || size.w === 0) return;
    const ctx = c.getContext('2d');
    if (ctx === null) return;
    if (readOnly) {
      ctx.clearRect(0, 0, c.width, c.height);
      if (value === undefined || value === null || value === '') return;
      const img = new Image();
      img.onload = (): void => {
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0, c.width, c.height);
      };
      img.src = value;
      return;
    }
    if (baselineLoadedRef.current) {
      redrawAll();
      return;
    }
    if (value === lastEmittedRef.current) return;
    if (value !== undefined && value !== null && value !== '') {
      const img = new Image();
      img.onload = (): void => {
        baselineImgRef.current = img;
        baselineLoadedRef.current = true;
        redrawAll();
      };
      img.src = value;
    }
  }, [value, size, readOnly, redrawAll]);

  const eventPoint = useCallback((e: PointerEvent): { x: number; y: number } | null => {
    const c = drawCanvasRef.current;
    if (c === null) return null;
    const rect = c.getBoundingClientRect();
    const x = ((e.clientX - rect.left) * c.width) / rect.width;
    const y = ((e.clientY - rect.top) * c.height) / rect.height;
    return { x, y };
  }, []);

  const startStroke = useCallback(
    (e: PointerEvent): void => {
      const p = eventPoint(e);
      const c = drawCanvasRef.current;
      if (p === null || c === null) return;
      drawingRef.current = true;
      lastPointRef.current = p;
      currentRef.current = [{ nx: p.x / c.width, ny: p.y / c.height }];
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [eventPoint],
  );

  const moveStroke = useCallback(
    (e: PointerEvent): void => {
      if (!drawingRef.current) return;
      const p = eventPoint(e);
      const c = drawCanvasRef.current;
      const last = lastPointRef.current;
      if (p === null || c === null || last === null) return;
      const ctx = c.getContext('2d');
      if (ctx === null) return;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastPointRef.current = p;
      currentRef.current.push({ nx: p.x / c.width, ny: p.y / c.height });
    },
    [eventPoint, strokeColor, strokeWidth, dpr],
  );

  const endStroke = useCallback((): void => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    if (currentRef.current.length > 0) {
      opsRef.current.push({ kind: 'stroke', points: currentRef.current });
      currentRef.current = [];
      setOpCount(opsRef.current.length);
      emit();
    }
  }, [emit]);

  useEffect(() => {
    const c = drawCanvasRef.current;
    if (c === null || readOnly) return;
    c.style.touchAction = 'none';
    c.addEventListener('pointerdown', startStroke);
    c.addEventListener('pointermove', moveStroke);
    c.addEventListener('pointerup', endStroke);
    c.addEventListener('pointerleave', endStroke);
    c.addEventListener('pointercancel', endStroke);
    return () => {
      c.removeEventListener('pointerdown', startStroke);
      c.removeEventListener('pointermove', moveStroke);
      c.removeEventListener('pointerup', endStroke);
      c.removeEventListener('pointerleave', endStroke);
      c.removeEventListener('pointercancel', endStroke);
    };
  }, [startStroke, moveStroke, endStroke, readOnly]);

  const undo = (): void => {
    if (opsRef.current.length === 0) return;
    opsRef.current.pop();
    setOpCount(opsRef.current.length);
    redrawAll();
    emit();
  };

  const clear = (): void => {
    opsRef.current = [];
    currentRef.current = [];
    baselineImgRef.current = null;
    baselineLoadedRef.current = true; // no recargar `value` como base tras borrar
    setOpCount(0);
    const c = drawCanvasRef.current;
    const ctx = c?.getContext('2d') ?? null;
    if (c !== null && ctx !== null) ctx.clearRect(0, 0, c.width, c.height);
    onChange?.('');
  };

  const stampSymbol = (glyph: string, nx: number, ny: number): void => {
    const c = drawCanvasRef.current;
    const ctx = c?.getContext('2d') ?? null;
    if (c === null || ctx === null) return;
    drawStamp(ctx, glyph, nx * c.width, ny * c.height);
    opsRef.current.push({ kind: 'stamp', glyph, nx, ny });
    setOpCount(opsRef.current.length);
    emit();
  };

  /* --- Arrastre de símbolo desde la paleta (mouse + touch + stylus) --- */
  const onSymbolPointerDown = (e: React.PointerEvent, glyph: string): void => {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragGlyphRef.current = glyph;
    setDrag({ glyph, x: e.clientX, y: e.clientY });
  };
  const onSymbolPointerMove = (e: React.PointerEvent): void => {
    if (dragGlyphRef.current === null) return;
    const x = e.clientX;
    const y = e.clientY;
    setDrag((d) => (d !== null ? { ...d, x, y } : d));
  };
  const onSymbolPointerUp = (e: React.PointerEvent): void => {
    const glyph = dragGlyphRef.current;
    dragGlyphRef.current = null;
    setDrag(null);
    if (glyph === null) return;
    const c = drawCanvasRef.current;
    if (c === null) return;
    const rect = c.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return; // soltó fuera de la gráfica
    stampSymbol(glyph, nx, ny);
  };

  const canUndo = opCount > 0;
  const cssW = size.w;
  const cssH = size.h;
  const pxW = Math.floor(cssW * dpr);
  const pxH = Math.floor(cssH * dpr);
  const showPalette = withSymbols && !readOnly;

  return (
    <div className="w-full">
      <div className="flex gap-3 items-start">
        {showPalette && (
          <div className="flex flex-col gap-1.5 shrink-0 select-none">
            <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
              Símbolos
            </div>
            {ANESTHESIA_CHART_SYMBOLS.map((s) => (
              <button
                key={s.id}
                type="button"
                title={`Arrastra "${s.label}" a la gráfica`}
                onPointerDown={(e) => onSymbolPointerDown(e, s.glyph)}
                onPointerMove={onSymbolPointerMove}
                onPointerUp={onSymbolPointerUp}
                className="w-9 h-9 grid place-items-center rounded-md border border-border bg-surface text-lg leading-none text-slate-900 hover:border-primary hover:bg-primary/5 cursor-grab active:cursor-grabbing touch-none"
                style={{ fontFamily: SYMBOL_FONT_STACK }}
              >
                {s.glyph}
              </button>
            ))}
          </div>
        )}

        {/* `isolation: isolate` acota el mix-blend-mode del canvas de dibujo. */}
        <div
          ref={wrapperRef}
          className="relative flex-1 min-w-0"
          style={{ isolation: 'isolate' }}
        >
          <canvas
            ref={bgCanvasRef}
            width={pxW}
            height={pxH}
            style={{ width: cssW, height: cssH, display: 'block', borderRadius: 6 }}
          />
          <canvas
            ref={drawCanvasRef}
            width={pxW}
            height={pxH}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: cssW,
              height: cssH,
              touchAction: readOnly ? 'auto' : 'none',
              cursor: readOnly ? 'default' : 'crosshair',
              pointerEvents: readOnly ? 'none' : 'auto',
              // Los registros firmados pudieron guardar el dibujo con fondo
              // blanco opaco; en solo-lectura mezclamos con `multiply` para
              // dejar ver la grilla (con fondo transparente es inocuo).
              mixBlendMode: readOnly ? 'multiply' : 'normal',
            }}
            aria-label={
              readOnly
                ? 'Gráfica de signos vitales del anestesiólogo'
                : 'Grilla de signos vitales — dibuja libremente'
            }
          />
        </div>
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between mt-2 text-[11px] text-text-muted gap-2 flex-wrap">
          <span>
            {showPalette
              ? 'Dibuja con dedo o stylus. Arrastra los símbolos del costado a la gráfica (puedes poner los que quieras).'
              : variant === 'v2'
                ? 'Dibuja con dedo o stylus. Filas: HORA / SpO2 / EKG / L.E.V. · eje vertical 0–200.'
                : 'Dibuja con dedo o stylus. Eje vertical: 0–240. Filas: INICIO / SAT O2 / Et CO2 / T° / O2 / N2O.'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo}
              className="px-3 py-1 rounded-md border border-border bg-surface text-xs font-semibold hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:text-text-muted"
            >
              Deshacer
            </button>
            <button
              type="button"
              onClick={clear}
              className="px-3 py-1 rounded-md border border-border bg-surface text-xs font-semibold hover:border-danger hover:text-danger"
            >
              Borrar dibujo
            </button>
          </div>
        </div>
      )}

      {drag !== null && (
        <div
          className="fixed z-50 text-xl font-bold text-slate-900 pointer-events-none select-none"
          style={{
            left: drag.x,
            top: drag.y,
            transform: 'translate(-50%, -50%)',
            fontFamily: SYMBOL_FONT_STACK,
          }}
        >
          {drag.glyph}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Painting helpers                                                           */
/* -------------------------------------------------------------------------- */

function paintGrid(
  canvas: HTMLCanvasElement,
  cssW: number,
  cssH: number,
  variant: 'v1' | 'v2',
): void {
  if (variant === 'v2') {
    paintGridV2(canvas, cssW, cssH);
    return;
  }
  paintGridV1(canvas, cssW, cssH);
}

function paintGridV1(canvas: HTMLCanvasElement, cssW: number, cssH: number): void {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cssW, cssH);

  const labelW = 52;
  const footerH = FOOTER_ROWS.length * 22;
  const plotX = labelW;
  const plotY = 0;
  const plotW = cssW - labelW - 8;
  const plotH = cssH - footerH - 24;

  ctx.fillStyle = GRID_BG_COLOR;
  ctx.fillRect(plotX, plotY, plotW, plotH);

  ctx.font = LABEL_FONT;
  ctx.fillStyle = LABEL_COLOR;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  Y_LABELS.forEach((label, idx) => {
    const y = plotY + (plotH / (Y_LABELS.length - 1)) * idx;
    ctx.strokeStyle = label === '0' || label === '240' ? GRID_LINE_DARK : GRID_LINE_MEDIUM;
    ctx.lineWidth = label === '0' || label === '240' ? 1 : 0.8;
    ctx.beginPath();
    ctx.moveTo(plotX, y);
    ctx.lineTo(plotX + plotW, y);
    ctx.stroke();
    ctx.fillStyle = LABEL_COLOR;
    ctx.fillText(label, plotX - 6, y);
  });

  const colW = plotW / X_COLUMNS;
  for (let i = 0; i <= X_COLUMNS; i++) {
    const x = plotX + colW * i;
    ctx.strokeStyle = i % 4 === 0 ? GRID_LINE_DARK : GRID_LINE_LIGHT;
    ctx.lineWidth = i % 4 === 0 ? 1 : 0.6;
    ctx.beginPath();
    ctx.moveTo(x, plotY);
    ctx.lineTo(x, plotY + plotH);
    ctx.stroke();
  }
  for (let i = 0; i < X_COLUMNS; i++) {
    const x = plotX + colW * (i + 0.5);
    ctx.strokeStyle = GRID_LINE_LIGHT;
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(x, plotY);
    ctx.lineTo(x, plotY + plotH);
    ctx.stroke();
  }

  ctx.strokeStyle = GRID_LINE_DARK;
  ctx.lineWidth = 1;
  ctx.strokeRect(plotX, plotY, plotW, plotH);

  FOOTER_ROWS.forEach((label, idx) => {
    const y = plotY + plotH + 22 * idx;
    ctx.fillStyle = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
    ctx.fillRect(plotX, y, plotW, 22);
    ctx.fillStyle = '#1f2937';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 10px Inter, system-ui, sans-serif';
    ctx.fillText(label, 6, y + 11);
    ctx.strokeStyle = GRID_LINE_MEDIUM;
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(plotX, y);
    ctx.lineTo(plotX + plotW, y);
    ctx.stroke();
    for (let c = 0; c <= X_COLUMNS; c++) {
      const x = plotX + colW * c;
      ctx.strokeStyle = c % 4 === 0 ? GRID_LINE_DARK : GRID_LINE_LIGHT;
      ctx.lineWidth = c % 4 === 0 ? 0.8 : 0.4;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + 22);
      ctx.stroke();
    }
  });
  ctx.strokeStyle = GRID_LINE_DARK;
  ctx.lineWidth = 1;
  ctx.strokeRect(plotX, plotY + plotH, plotW, footerH);

  ctx.fillStyle = '#1f2937';
  ctx.font = 'bold 10px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('HORA', 6, 2);
}

/**
 * Grilla del papel NUEVO: bandas superiores HORA / SpO2 / EKG / L.E.V. y, debajo,
 * el eje vertical 200 → 0 en pasos de 10 con columnas de tiempo.
 */
function paintGridV2(canvas: HTMLCanvasElement, cssW: number, cssH: number): void {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cssW, cssH);

  const labelW = 56;
  const headerRowH = 22;
  const headerH = HEADER_ROWS_V2.length * headerRowH;
  const plotX = labelW;
  const plotW = cssW - labelW - 8;
  const plotY = headerH;
  const plotH = cssH - headerH - 12;
  const colW = plotW / X_COLUMNS_V2;

  HEADER_ROWS_V2.forEach((label, idx) => {
    const y = headerRowH * idx;
    ctx.fillStyle = idx % 2 === 0 ? '#ffffff' : GRID_BG_COLOR;
    ctx.fillRect(0, y, cssW - 8, headerRowH);
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 10px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 6, y + headerRowH / 2);
    ctx.strokeStyle = GRID_LINE_MEDIUM;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(cssW - 8, y);
    ctx.stroke();
    for (let c = 0; c <= X_COLUMNS_V2; c++) {
      const x = plotX + colW * c;
      ctx.strokeStyle = c % 4 === 0 ? GRID_LINE_DARK : GRID_LINE_LIGHT;
      ctx.lineWidth = c % 4 === 0 ? 0.8 : 0.4;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + headerRowH);
      ctx.stroke();
    }
  });
  ctx.strokeStyle = GRID_LINE_DARK;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(plotX, 0);
  ctx.lineTo(plotX, headerH);
  ctx.stroke();
  ctx.strokeRect(0, 0, cssW - 8, headerH);

  ctx.fillStyle = GRID_BG_COLOR;
  ctx.fillRect(plotX, plotY, plotW, plotH);

  const steps = Y_MAX_V2 / Y_STEP_V2;
  ctx.font = '9px Inter, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= steps; i++) {
    const val = Y_MAX_V2 - i * Y_STEP_V2;
    const y = plotY + (plotH / steps) * i;
    const isMajor = val % 50 === 0;
    ctx.strokeStyle = isMajor ? GRID_LINE_DARK : GRID_LINE_MEDIUM;
    ctx.lineWidth = isMajor ? 1 : 0.6;
    ctx.beginPath();
    ctx.moveTo(plotX, y);
    ctx.lineTo(plotX + plotW, y);
    ctx.stroke();
    ctx.fillStyle = LABEL_COLOR;
    ctx.fillText(String(val), plotX - 6, y);
  }

  for (let c = 0; c <= X_COLUMNS_V2; c++) {
    const x = plotX + colW * c;
    ctx.strokeStyle = c % 4 === 0 ? GRID_LINE_DARK : GRID_LINE_LIGHT;
    ctx.lineWidth = c % 4 === 0 ? 1 : 0.5;
    ctx.beginPath();
    ctx.moveTo(x, plotY);
    ctx.lineTo(x, plotY + plotH);
    ctx.stroke();
  }

  ctx.strokeStyle = GRID_LINE_DARK;
  ctx.lineWidth = 1;
  ctx.strokeRect(plotX, plotY, plotW, plotH);
}
